import { Suspense, lazy } from "react";
import { Navigate, useRoutes } from "react-router-dom";
// layouts
import DashboardLayout from "../layouts";
import LogoOnlyLayout from "../layouts/LogoOnlyLayout";
// components
import LoadingScreen from "../components/LoadingScreen";
import { ROUTES } from "./paths";

const Loadable = (Component) => (props) => {
  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...{
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: "fixed",
            },
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    // Dashboard Routes
    {
      element: <DashboardLayout />,
      children: [
        {
          path: "",
          element: <Navigate to={ROUTES.general.welcome} replace />,
        },
        { path: "welcome", element: <WelcomePage /> },
        {
          path: "location",
          children: [
            { path: "list", element: <LocationList /> },
            { path: "new", element: <LocationCreate /> },
            { path: ":id", element: <LocationShow /> },
            { path: ":id/edit", element: <LocationEdit /> },
          ],
        },
        {
          path: "color",
          children: [
            { path: "list", element: <ColorList /> },
            { path: "new", element: <ColorCreate /> },
            { path: ":id", element: <ColorEdit /> },
          ],
        },
        {
          path: "category",
          children: [
            { path: "list", element: <CategoryList /> },
            { path: "new", element: <CategoryCreate /> },
            { path: ":id", element: <CategoryEdit /> },
          ],
        },
        {
          path: "product",
          children: [
            { path: "list", element: <ProductsList /> },
            { path: "new", element: <ProductCreate /> },
            { path: ":id", element: <ProductShow /> },
            { path: ":id/edit", element: <ProductEdit /> },
          ],
        },
        {
          path: "order",
          children: [
            { path: "list", element: <OrderList /> },
            { path: ":id", element: <OrderShow /> },
            { path: ":id/edit", element: <OrderEdit /> },
          ],
        },
        {
          path: "price/coupon",
          children: [
            { path: "list", element: <CouponList /> },
            { path: "new", element: <CouponCreate /> },
            { path: ":id", element: <CouponShow /> },
            { path: ":id/edit", element: <CouponEdit /> },
          ],
        },
        {
          path: "price/discount",
          children: [
            { path: "list", element: <DiscountList /> },
            { path: "new", element: <DiscountCreate /> },
            { path: ":id", element: <DiscountShow /> },
            { path: ":id/edit", element: <DiscountEdit /> },
          ],
        },
        {
          path: "customer",
          children: [
            { path: "all", element: <CustomerList /> },
            {
              path: "with-purchases",
              element: <CustomerList filter={"with_purchases"} />,
            },
            {
              path: "subscribed-for-newsletter",
              element: <CustomerList filter={"subscribed_for_newsletter"} />,
            },
            { path: ":id/edit", element: <UserEdit /> },
          ],
        },
        {
          path: "user",
          children: [
            { path: "list", element: <UserList /> },
            { path: "new", element: <UserCreate /> },
            { path: ":id/edit", element: <UserEdit /> },
          ],
        },
        {
          path: "special-offer",
          children: [
            { path: "list", element: <SpecialOffersList /> },
            { path: "new", element: <SpecialOfferCreate /> },
            { path: ":id", element: <SpecialOfferShow /> },
            { path: ":id/edit", element: <SpecialOfferEdit /> },
          ],
        },
        {
          path: "page",
          children: [
            { path: "list", element: <PageList /> },
            { path: "new", element: <PageCreate /> },
            { path: ":id/edit", element: <PageEdit /> },
            { path: ":id", element: <PageShow /> },
          ],
        },
      ],
    },

    // auth
    { path: `/login`, element: <Login /> },

    // Main Routes
    {
      element: <LogoOnlyLayout />,
      children: [
        { path: `/404`, element: <NotFound /> },
        {
          path: "*",
          element: <Navigate to={ROUTES.page404} replace />,
        },
      ],
    },
  ]);
}

// IMPORT COMPONENTS

// auth
const Login = Loadable(lazy(() => import("../pages/Auth/Login")));

// Admin
const WelcomePage = Loadable(lazy(() => import("../pages/WelcomePage")));
const NotFound = Loadable(lazy(() => import("../pages/Page404")));

// products
const ProductsList = Loadable(
  lazy(() => import("../pages/Product/ProductList"))
);
const ProductEdit = Loadable(
  lazy(() => import("../pages/Product/ProductEdit"))
);
const ProductCreate = Loadable(
  lazy(() => import("../pages/Product/ProductCreate"))
);
const ProductShow = Loadable(
  lazy(() => import("../pages/Product/ProductShow"))
);

// users
const UserList = Loadable(lazy(() => import("../pages/User/UserList")));
const UserCreate = Loadable(lazy(() => import("../pages/User/UserCreate")));
const UserEdit = Loadable(lazy(() => import("../pages/User/UserEdit")));

// customers
const CustomerList = Loadable(
  lazy(() => import("../pages/Customer/CustomerList"))
);

//locations
const LocationList = Loadable(
  lazy(() => import("../pages/Location/LocationList"))
);
const LocationCreate = Loadable(
  lazy(() => import("../pages/Location/LocationCreate"))
);
const LocationEdit = Loadable(
  lazy(() => import("../pages/Location/LocationEdit"))
);
const LocationShow = Loadable(
  lazy(() => import("../pages/Location/LocationShow"))
);

//colors
const ColorList = Loadable(lazy(() => import("../pages/Color/ColorList")));
const ColorCreate = Loadable(lazy(() => import("../pages/Color/ColorCreate")));
const ColorEdit = Loadable(lazy(() => import("../pages/Color/ColorEdit")));

//pages
const PageList = Loadable(lazy(() => import("../pages/Page/PageList")));
const PageCreate = Loadable(lazy(() => import("../pages/Page/PageCreate")));
const PageEdit = Loadable(lazy(() => import("../pages/Page/PageEdit")));
const PageShow = Loadable(lazy(() => import("../pages/Page/PageShow")));

//categories
const CategoryList = Loadable(
  lazy(() => import("../pages/Category/CategoryList"))
);
const CategoryCreate = Loadable(
  lazy(() => import("../pages/Category/CategoryCreate"))
);
const CategoryEdit = Loadable(
  lazy(() => import("../pages/Category/CategoryEdit"))
);

// orders
const OrderList = Loadable(lazy(() => import("../pages/Order/OrderList")));
const OrderShow = Loadable(lazy(() => import("../pages/Order/OrderShow")));
const OrderEdit = Loadable(lazy(() => import("../pages/Order/OrderEdit")));

// special offers
const SpecialOffersList = Loadable(
  lazy(() => import("../pages/SpecialOffer/SpecialOfferList"))
);
const SpecialOfferEdit = Loadable(
  lazy(() => import("../pages/SpecialOffer/SpecialOfferEdit"))
);
const SpecialOfferCreate = Loadable(
  lazy(() => import("../pages/SpecialOffer/SpecialOfferCreate"))
);
const SpecialOfferShow = Loadable(
  lazy(() => import("../pages/SpecialOffer/SpecialOfferShow"))
);

// coupons
const CouponList = Loadable(lazy(() => import("../pages/Coupon/CouponList")));
const CouponEdit = Loadable(lazy(() => import("../pages/Coupon/CouponEdit")));
const CouponCreate = Loadable(
  lazy(() => import("../pages/Coupon/CouponCreate"))
);
const CouponShow = Loadable(lazy(() => import("../pages/Coupon/CouponShow")));

// discounts
const DiscountList = Loadable(
  lazy(() => import("../pages/Discount/DiscountList"))
);
const DiscountEdit = Loadable(
  lazy(() => import("../pages/Discount/DiscountEdit"))
);
const DiscountCreate = Loadable(
  lazy(() => import("../pages/Discount/DiscountCreate"))
);
const DiscountShow = Loadable(
  lazy(() => import("../pages/Discount/DiscountShow"))
);
